import { LocationDescriptor } from 'history';
import _ from 'lodash';
import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { insightsFilterRouteFragment } from 'src/app/graph/routes';
import Config from 'src/lib/config';
import {
  RouteConfig,
  RouteConfigParams,
} from 'src/lib/routeConfig/RouteConfig';

import { useRouteParams } from './useRouteParams';

export const fieldFilterDefaults = {
  [Config.queryParameters.FieldListAfterCursor]: undefined,
  [Config.queryParameters.FieldListBeforeCursor]: undefined,
  [Config.queryParameters.FieldListMetricFilter]: undefined,
  [Config.queryParameters.FieldListMetricSort]: undefined,
  [Config.queryParameters.FieldListSortDirection]: undefined,
  [Config.queryParameters.FieldListSearchFilter]: undefined,
  [Config.queryParameters.FieldListPage]: undefined,
};

export const operationFilterDefaults = {
  [Config.queryParameters.OperationListAfterCursor]: undefined,
  [Config.queryParameters.OperationListBeforeCursor]: undefined,
  [Config.queryParameters.OperationListMetricFilter]: undefined,
  [Config.queryParameters.OperationListMetricFilters]: undefined,
  [Config.queryParameters.OperationListMetricSort]: undefined,
  [Config.queryParameters.OperationListSortDirection]: undefined,
  [Config.queryParameters.OperationListSearchFilter]: undefined,
  [Config.queryParameters.OperationListPage]: undefined,
};

export const subgraphFilterDefaults = {
  [Config.queryParameters.SubgraphListSearchFilter]: undefined,
  [Config.queryParameters.SubgraphListAfterCursor]: undefined,
  [Config.queryParameters.SubgraphListBeforeCursor]: undefined,
  [Config.queryParameters.SubgraphListSortDirection]: undefined,
  [Config.queryParameters.SubgraphListMetricSort]: undefined,
  [Config.queryParameters.SubgraphListPage]: undefined,
};

export const clientFilterDefaults = {
  [Config.queryParameters.ClientFilter]: undefined,
};

export const timeRangeFilterDefaults = {
  [Config.queryParameters.SelectedTimeRange]: undefined,
  [Config.queryParameters.SelectedTimeRangeFrom]: undefined,
  [Config.queryParameters.SelectedTimeRangeTo]: undefined,
  [Config.queryParameters.FastModeRange]: undefined,
};

export const errorsFilterDefaults = {
  [Config.queryParameters.ErrorsBreakdown]: undefined,
  [Config.queryParameters.ErrorsGroupBy]: undefined,
  [Config.queryParameters.ErrorsCodeFilter]: undefined,
  [Config.queryParameters.ErrorsServiceFilter]: undefined,
  [Config.queryParameters.ErrorsClientNameFilter]: undefined,
  [Config.queryParameters.ErrorsPathFilter]: undefined,
  [Config.queryParameters.ErrorsDrilldown]: undefined,
  [Config.queryParameters.ErrorsView]: undefined,
};

export type InsightsFilterParams = RouteConfigParams<
  typeof insightsFilterRouteFragment
>;

/**
 * Keep track of the "defaults" that we want to use when deciding whether or not
 * the filter can be reset (i.e. if it's been modified from the defaults).
 * Potentially this could support having defaults with values other than
 * undefined but so far we haven't needed that. A good first candidate could be
 * range=lastDay.
 */
const insightsDefaultParams: InsightsFilterParams = {
  // Shared params - we only "reset" these to default when using the
  // operations overview button.
  [Config.queryParameters.SelectedTraceId]: undefined,
  [Config.queryParameters.SelectedDurationBucket]: undefined,
  [Config.queryParameters.SelectedQueryId]: undefined,
  [Config.queryParameters.SelectedQueryName]: undefined,

  // Fields params
  ...fieldFilterDefaults,

  // Operations params
  ...operationFilterDefaults,

  // Legacy operations params - we should make sure to redirect from these to the new ones.
  // Once we are confident that old URLs will work correctly, we should be
  // able to remove these.
  [Config.queryParameters.QueryListMetricSort]: undefined,
  [Config.queryParameters.QueryListMetricFilter]: undefined,

  // Time range params
  ...timeRangeFilterDefaults,

  // Client filter params
  ...clientFilterDefaults,

  // Errors params
  ...errorsFilterDefaults,
};

function convertToRoutePatch(params: InsightsFilterParams) {
  return _.mapValues(params, (value) =>
    value === undefined ? RouteConfig.DELETE : value,
  );
}

function isModifiedFromDefaults(
  params: InsightsFilterParams,
  defaultParams: InsightsFilterParams,
) {
  let isModified = false;
  for (const [k, v] of Object.entries(defaultParams)) {
    isModified = isModified || params[k] !== v;
    if (isModified) {
      break;
    }
  }
  return isModified;
}

export function convertClientFilterToStatsFilter(
  clientFilterFromRoute: InsightsFilterParams['clients'],
) {
  const entries = clientFilterFromRoute
    ? Object.entries(clientFilterFromRoute)
    : [];
  return entries.map(([name, versions]) => ({
    clientName: name,
    ...(Array.isArray(versions) && {
      in: {
        clientVersion: versions,
      },
    }),
  }));
}

export function convertClientFilterToErrorInsightsFilter(
  clientFilterFromRoute: InsightsFilterParams['clients'],
) {
  const entries = clientFilterFromRoute
    ? Object.entries(clientFilterFromRoute)
    : [];
  return entries.flatMap(([name, versions]) =>
    Array.isArray(versions)
      ? versions.map((v) => ({ clientName: name, clientVersion: v }))
      : [{ clientName: name }],
  );
}

export interface InsightsFilterHook {
  /**
   * Pass-thru from useRouteParams(insightsFilterRouteFragment)
   */
  routeParams: InsightsFilterParams;
  /**
   * The set of param values considered to be the "default".  Used for
   * determining whether the state can be reset or not.
   */
  defaultParams: InsightsFilterParams;
  /**
   * Whether there are any filters set to different values than the defaults.
   */
  hasActiveFilters: boolean;
  /**
   * Whether there are any filters set to different values than the defaults
   * that are passed in (e.g. useful to see if just the client filter is active)
   * via `hasActiveFiltersForSubset(clientFilterDefaults)`
   */
  hasActiveFiltersForSubset: (
    specificDefaultParams: InsightsFilterParams,
  ) => boolean;
  /**
   * Convenience method to navigate to a new location based on the passed-in
   * parameters and routeConfig.
   */
  setFilterParams: <TRouteConfig>(
    routeConfig: InsightsFilterRouteConfig<TRouteConfig>,
    newFilterParams: InsightsFilterParams,
  ) => void;
  /**
   * Convenience method to compute a location to redirect to
   * when resetting to default filters based on the passed-in routeConfig.
   */
  resetToDefaultLocation: <TRouteConfig>(
    routeConfig: InsightsFilterRouteConfig<TRouteConfig>,
  ) => LocationDescriptor;
}

// Ideally we'd narrow this to route configs that have overlapping properties
// with the insightsFilterRouteFragment but haven't quite figured that out.
type InsightsFilterRouteConfig<T> =
  T extends RouteConfig<
    infer _MatchParams,
    infer _SearchParams,
    infer _State,
    infer _Hash,
    infer _Definition
  >
    ? T
    : never;

/**
 * Provides params from route config pertaining to any "insights" UX,
 * as well as helping to determine if there are any active insights filters.
 * @param defaultParams An object that represents the set of defaults to be
 * used when computing whether the filter is active.  Also used to compute the
 * location to redirect to when resetting params to their default values.
 */
export const useInsightsFilter = (
  defaultParams: InsightsFilterParams = insightsDefaultParams,
): InsightsFilterHook => {
  const location = useLocation();
  const history = useHistory();
  const params = useRouteParams(insightsFilterRouteFragment);
  const resetToDefaultLocation = useCallback(
    <TRouteConfig>(routeConfig: InsightsFilterRouteConfig<TRouteConfig>) =>
      routeConfig.locationFrom({
        location,
        patch: convertToRoutePatch(defaultParams),
      }),
    [defaultParams, location],
  );
  const setFilterParams = useCallback(
    <TRouteConfig>(
      routeConfig: InsightsFilterRouteConfig<TRouteConfig>,
      newFilterParams: InsightsFilterParams,
    ) =>
      history.push(
        routeConfig.locationFrom({
          location,
          patch: convertToRoutePatch(newFilterParams),
        }),
      ),
    [history, location],
  );
  const hasActiveFilters = isModifiedFromDefaults(params, defaultParams);
  const hasActiveFiltersForSubset = useCallback(
    (specificDefaultParams: InsightsFilterParams) =>
      isModifiedFromDefaults(params, specificDefaultParams),
    [params],
  );
  return {
    routeParams: params,
    defaultParams,
    hasActiveFilters,
    hasActiveFiltersForSubset,
    setFilterParams,
    resetToDefaultLocation,
  };
};

/**
 * Provides params from route config pertaining to the "fields" UX,
 * as well as determining if there are any active fields filters.
 */
export const useFieldInsightsFilter = (): InsightsFilterHook => {
  const fieldDefaults = {
    ...fieldFilterDefaults,
    ...timeRangeFilterDefaults,
    ...clientFilterDefaults,
  };
  return useInsightsFilter(fieldDefaults);
};

/**
 * Provides params from route config pertaining to the "operations" UX,
 * as well as determining if there are any active operations filters.
 */
export const useOperationInsightsFilter = (): InsightsFilterHook => {
  const operationDefaults = {
    ...operationFilterDefaults,
    ...timeRangeFilterDefaults,
    ...clientFilterDefaults,
    ...errorsFilterDefaults,
  };
  return useInsightsFilter(operationDefaults);
};

/**
 * Provides params from route config pertaining to the "subgraphs" UX,
 * as well as determining if there are any active subgraphs filters.
 */
export const useSubgraphInsightsFilter = (): InsightsFilterHook => {
  const subgraphDefaults = {
    ...subgraphFilterDefaults,
    ...timeRangeFilterDefaults,
    ...clientFilterDefaults,
    ...errorsFilterDefaults,
  };
  return useInsightsFilter(subgraphDefaults);
};
